import { produce } from 'immer'
import {
  REMOVE_ORGANIZATION_INVITE,
  RemoveOrganizationInviteActions,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import {
  ApprovalRuleResponse,
  OrganizationInviteResponse,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import {
  ABANDON_CREATE_ORGANIZATION,
  ABANDON_EDIT_ORGANIZATION,
  DELETE_ORGANIZATION_CARD_APPROVALS_ENABLED_SUCCESS,
  DeleteOrganizationActions,
  DeleteOrganizationCardApprovalsEnabledActions,
  EDIT_ORGANIZATION_INIT,
  GET_ORGANIZATION_CARD_APPROVALS_ENABLED_SUCCESS,
  GET_ORGANIZATION_CURRENCIES_SUCCESS,
  GET_ORGANIZATION_INTEGRATIONS_SUCCESS,
  GET_ORGANIZATION_INVITE_SUCCESS,
  GET_ORGANIZATION_INVITES_SUCCESS,
  GET_ORGANIZATION_OUT_OF_POCKET_EXPENSES_ENABLED_SUCCESS,
  GET_ORGANIZATION_RECEIPT_MGMT_ENABLED_SUCCESS,
  GET_ORGANIZATION_SUCCESS,
  GET_ORGANIZATIONS_SUCCESS,
  GET_RECEIPT_RULES_SUCCESS,
  GET_USER_ORGANIZATION_INVITES_SUCCESS,
  GetOrganizationActions,
  GetOrganizationCardApprovalsEnabledActions,
  GetOrganizationCurrenciesActions,
  GetOrganizationIntegrationsActions,
  GetOrganizationInviteActions,
  GetOrganizationInvitesActions,
  GetOrganizationOutOfPocketExpensesEnabledActions,
  GetOrganizationReceiptMgmtEnabledActions,
  GetOrganizationsActions,
  GetOrganizationSuccessAction,
  GetReceiptRulesActions,
  GetUserOrganizationInvitesActions,
  InviteOrganizationActions,
  PATCH_ORGANIZATIONS_SUCCESS,
  PatchOrganizationsActions,
  POST_ORGANIZATION_CARD_APPROVALS_ENABLED_FAILURE,
  POST_ORGANIZATION_CARD_APPROVALS_ENABLED_INIT,
  POST_ORGANIZATION_CARD_APPROVALS_ENABLED_SUCCESS,
  POST_ORGANIZATION_FAILURE,
  POST_ORGANIZATION_INIT,
  POST_ORGANIZATION_SUCCESS,
  POST_RECEIPT_RULES_SUCCESS,
  PostOrganizationActions,
  PostOrganizationCardApprovalsEnabledActions,
  PostReceiptRulesActions,
  PUT_ORGANIZATION_FAILURE,
  PUT_ORGANIZATION_INIT,
  PUT_ORGANIZATION_SUCCESS,
  PUT_OUT_OF_POCKET_EXPENSES_SUCCESS,
  PUT_RECEIPT_RULES_SUCCESS,
  PutOrganizationActions,
  PutOutOfPocketExpensesActions,
  PutReceiptRulesActions,
  SET_UPDATED_RECEIPT_RULES,
  SetUpdatedReceiptRules,
} from 'src/redux/actions/data/organizations.actions'
import OrgExpenseReportsReducer, {
  Actions as GetOrgExpenseReportsActions,
  initialState as initialOrgExpenseReportsState,
  OrgExpenseReportsState,
} from 'src/redux/reducers/data/orgExpenseReports.reducer'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface OrganizationInvitesState {
  listResponses: Record<string, OrganizationInviteResponse>
}

export interface OrganizationReceiptRulesState {
  enabled?: boolean
  isLoaded: boolean
  updatedReceiptRules: boolean
}

export interface OrganizationsState {
  listResponses: Record<string, OrganizationsListResponse>
  totalItems?: number
  isOrganizationBeingEdited: boolean
  patchingOrganizations: boolean
  puttingOrganizations: boolean
  patchOrganizationsTopLevelError?: string
  putOrganizationsTopLevelError?: string
  organizationInvites: OrganizationInvitesState
  puttingOrganizationRole: boolean
  organizationReceiptRules: OrganizationReceiptRulesState
  postingOrganization?: boolean
  topLevelError?: string
  outOfPocketExpensesEnabled?: boolean
  cardApprovalRules?: ApprovalRuleResponse
  cardApprovalsIsFetched: boolean
  integrations: string[]
  reports: OrgExpenseReportsState
}

export const initialInviteState: OrganizationInvitesState = {
  listResponses: {},
}

export const initialOrganizationReceiptRulesState: OrganizationReceiptRulesState =
  {
    isLoaded: false,
    updatedReceiptRules: false,
  }

export const initialState: OrganizationsState = {
  listResponses: {},
  isOrganizationBeingEdited: false,
  patchingOrganizations: false,
  puttingOrganizations: false,
  totalItems: 0,
  organizationInvites: initialInviteState,
  puttingOrganizationRole: false,
  organizationReceiptRules: initialOrganizationReceiptRulesState,
  postingOrganization: false,
  integrations: [],
  reports: initialOrgExpenseReportsState,
  cardApprovalsIsFetched: false,
}

export type Actions =
  | GetOrganizationActions
  | GetOrganizationsActions
  | PatchOrganizationsActions
  | PostOrganizationActions
  | PutOrganizationActions
  | DeleteOrganizationActions
  | InviteOrganizationActions
  | GetUserOrganizationInvitesActions
  | GetOrganizationInviteActions
  | GetOrganizationInvitesActions
  | GetReceiptRulesActions
  | GetOrganizationCurrenciesActions
  | PostReceiptRulesActions
  | PutReceiptRulesActions
  | GetOrganizationReceiptMgmtEnabledActions
  | SetUpdatedReceiptRules
  | PutOutOfPocketExpensesActions
  | GetOrganizationOutOfPocketExpensesEnabledActions
  | GetOrganizationSuccessAction
  | GetOrganizationIntegrationsActions
  | RemoveOrganizationInviteActions
  | GetOrganizationCardApprovalsEnabledActions
  | PostOrganizationCardApprovalsEnabledActions
  | DeleteOrganizationCardApprovalsEnabledActions
  | GetOrgExpenseReportsActions

const OrganizationsReducer = (
  state: OrganizationsState = initialState,
  action: Actions,
): OrganizationsState => {
  const updateOrgWithIntegrationsAndReceiptRules = (
    oldOrg: OrganizationsListResponse,
    newOrg: OrganizationsListResponse,
  ): OrganizationsListResponse => {
    return oldOrg
      ? {
          ...newOrg,
          integrations:
            newOrg['integrations'] !== undefined
              ? newOrg['integrations']
              : oldOrg['integrations'],
          receiptRule:
            newOrg['receiptRule'] !== undefined
              ? newOrg['receiptRule']
              : oldOrg['receiptRule'],
        }
      : newOrg
  }

  return {
    ...produce(state, (draft) => {
      switch (action.type) {
        case GET_ORGANIZATIONS_SUCCESS: {
          draft.listResponses = action.payload.organizations.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.id]: updateOrgWithIntegrationsAndReceiptRules(
                draft.listResponses[curr.id],
                curr,
              ),
            }),
            draft.listResponses,
          )
          return draft
        }

        case POST_ORGANIZATION_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.organization.id]: action.payload.organization,
          }
          draft.postingOrganization = false
          return draft
        }

        case GET_ORGANIZATION_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.organization.id]: updateOrgWithIntegrationsAndReceiptRules(
              draft.listResponses[action.payload.organization.id],
              action.payload.organization,
            ),
          }
          return draft
        }

        case POST_ORGANIZATION_INIT: {
          draft.topLevelError = undefined
          draft.postingOrganization = true
          return draft
        }

        case ABANDON_CREATE_ORGANIZATION: {
          draft.topLevelError = undefined
          return draft
        }

        case POST_ORGANIZATION_FAILURE: {
          draft.postingOrganization = false
          const errors = parseBackendErrors(action.payload)
          draft.topLevelError = errors?.errorDetailMap
            ? `${Object.keys(errors.errorDetailMap)[0]} ${Object.values(errors.errorDetailMap)[0]}`
            : errors.topLevelError
          return draft
        }

        case PUT_ORGANIZATION_INIT: {
          draft.topLevelError = undefined
          draft.puttingOrganizations = true
          return draft
        }

        case EDIT_ORGANIZATION_INIT: {
          draft.topLevelError = undefined
          draft.isOrganizationBeingEdited = true
          return draft
        }
        case PUT_ORGANIZATION_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.organization.id]: {
              ...draft.listResponses[action.payload.organization.id],
              ...action.payload.organization,
            },
          }
          draft.puttingOrganizations = false
          draft.putOrganizationsTopLevelError = undefined
          draft.isOrganizationBeingEdited = false
          return draft
        }

        case PUT_ORGANIZATION_FAILURE: {
          draft.puttingOrganizations = true
          draft.topLevelError = parseBackendErrors(
            action.payload,
          ).errorDetailMap?.name[0]
          draft.isOrganizationBeingEdited = true
          return draft
        }

        case ABANDON_EDIT_ORGANIZATION: {
          draft.puttingOrganizations = false
          draft.topLevelError = undefined
          draft.isOrganizationBeingEdited = false
          return draft
        }

        case PATCH_ORGANIZATIONS_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.id]: action.payload,
          }
          draft.patchingOrganizations = false
          draft.patchOrganizationsTopLevelError = undefined
          return draft
        }

        case GET_ORGANIZATION_INVITE_SUCCESS: {
          draft.organizationInvites.listResponses = {
            ...draft.organizationInvites.listResponses,
            [action.payload.organizationInvite.id]:
              action.payload.organizationInvite,
          }
          return draft
        }

        case REMOVE_ORGANIZATION_INVITE: {
          delete draft.organizationInvites.listResponses[action.payload]
          return draft
        }

        case GET_ORGANIZATION_INVITES_SUCCESS:
        case GET_USER_ORGANIZATION_INVITES_SUCCESS: {
          draft.organizationInvites.listResponses =
            action.payload.organizationInvites.reduce(
              (acc, curr) => ({
                ...acc,
                [curr.id]: curr,
              }),
              {},
            )
          return draft
        }

        case GET_ORGANIZATION_RECEIPT_MGMT_ENABLED_SUCCESS: {
          draft.organizationReceiptRules.isLoaded = true
          draft.organizationReceiptRules.enabled =
            action.payload.receiptManagementEnabled
          return draft
        }

        case GET_ORGANIZATION_OUT_OF_POCKET_EXPENSES_ENABLED_SUCCESS: {
          draft.outOfPocketExpensesEnabled =
            action.payload.hasOutOfPocketExpenses
          return draft
        }

        case GET_ORGANIZATION_CARD_APPROVALS_ENABLED_SUCCESS: {
          if (action.payload?.approvalRules?.length > 0) {
            draft.cardApprovalRules = action.payload.approvalRules[0]
          }
          draft.cardApprovalsIsFetched = true
          return draft
        }

        case POST_ORGANIZATION_CARD_APPROVALS_ENABLED_INIT: {
          draft.topLevelError = undefined
          return draft
        }

        case POST_ORGANIZATION_CARD_APPROVALS_ENABLED_SUCCESS: {
          draft.cardApprovalRules = action.payload.approvalRule
          return draft
        }

        case POST_ORGANIZATION_CARD_APPROVALS_ENABLED_FAILURE: {
          draft.topLevelError = action.payload.error
          return draft
        }

        case DELETE_ORGANIZATION_CARD_APPROVALS_ENABLED_SUCCESS: {
          if (action.payload?.msg === 'OK') {
            draft.cardApprovalRules = undefined
          }
          return draft
        }

        case PUT_RECEIPT_RULES_SUCCESS:
        case POST_RECEIPT_RULES_SUCCESS: {
          draft.organizationReceiptRules.updatedReceiptRules =
            action.updateTransactionTable
          draft.listResponses = {
            ...draft.listResponses,
            [action.orgId]: {
              ...draft.listResponses[action.orgId],
              ...action.payload,
            },
          }
          return draft
        }

        case SET_UPDATED_RECEIPT_RULES: {
          draft.organizationReceiptRules.updatedReceiptRules = false
          return draft
        }

        case GET_RECEIPT_RULES_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.orgId]: {
              ...draft.listResponses[action.orgId],
              ...action.payload,
            },
          }
          return draft
        }

        case GET_ORGANIZATION_CURRENCIES_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.orgId]: {
              ...draft.listResponses[action.orgId],
              currencies: action.payload.currencies,
            },
          }

          return draft
        }

        case PUT_OUT_OF_POCKET_EXPENSES_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.orgId]: {
              ...draft.listResponses[action.orgId],
              expenseApprover: action.payload.organization.expenseApprover,
              isOutOfPocketExpensesVisible:
                action.payload.organization.isOutOfPocketExpensesVisible,
              mileageRate: action.payload.organization.mileageRate,
            },
          }

          return draft
        }
        case GET_ORGANIZATION_INTEGRATIONS_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.orgId]: {
              ...draft.listResponses[action.orgId],
              integrations: action.payload.integrations,
            },
          }
          return draft
        }
      }
    }),
    reports: OrgExpenseReportsReducer(
      state.reports,
      action as GetOrgExpenseReportsActions,
    ),
  }
}

export default OrganizationsReducer
